import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, inp, label, card, btnPrimary } from "./AdminShell";
import { Plus, Edit, Trash2, X, Upload } from "lucide-react";

/* ===================== CATEGORIES ===================== */
export function AdminCategories() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = () => api.get("/admin/categories").then((r) => setList(r.data)).finally(() => setLoading(false));
  useEffect(() => { refresh(); }, []);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/categories/${data.id}`, data);
      else await api.post("/admin/categories", data);
      toast.success("Kategori tersimpan.");
      setEditing(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Hapus kategori "${name}"?`)) return;
    try { await api.delete(`/admin/categories/${id}`); toast.success("Kategori dihapus."); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <div>
      <PageHeader title="Categories" subtitle="Kelola kategori produk yang tampil di navigasi & filter"
        actions={<Button onClick={() => setEditing({ name: "", slug: "", description: "", image: "", order: (list.length + 1) * 10, published: true, show_in_menu: true, show_on_homepage: true })} className={`${btnPrimary} h-10 px-4`} data-testid="new-category-btn"><Plus size={14} /> Kategori Baru</Button>} />

      <div className={`${card} overflow-hidden`}>
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-4 py-3">Nama</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Slug</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Urutan</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {loading ? <tr><td colSpan="5" className="text-center py-8 text-neutral-500">Memuat...</td></tr> :
              list.map((c) => (
                <tr key={c.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 font-semibold text-neutral-900">{c.name}</td>
                  <td className="px-4 py-3 text-neutral-500 font-mono text-xs hidden md:table-cell">{c.slug}</td>
                  <td className="px-4 py-3 text-neutral-500 hidden md:table-cell">{c.order}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-mono uppercase ${c.published ? "bg-green-50 text-green-700" : "bg-neutral-100 text-neutral-500"}`}>
                      {c.published ? "Aktif" : "Arsip"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(c)} className="text-neutral-500 hover:text-[#FF9418] p-1.5" data-testid={`edit-cat-${c.slug}`}><Edit size={14} /></button>
                    <button onClick={() => del(c.id, c.name)} className="text-neutral-500 hover:text-red-600 p-1.5" data-testid={`del-cat-${c.slug}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {editing && <TaxonomyModal title={editing.id ? "Edit Kategori" : "Kategori Baru"} data={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

/* ===================== BRANDS ===================== */
export function AdminBrands() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = () => api.get("/admin/brands").then((r) => setList(r.data)).finally(() => setLoading(false));
  useEffect(() => { refresh(); }, []);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/brands/${data.id}`, data);
      else await api.post("/admin/brands", data);
      toast.success("Brand tersimpan.");
      setEditing(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Hapus brand "${name}"?`)) return;
    try { await api.delete(`/admin/brands/${id}`); toast.success("Brand dihapus."); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <div>
      <PageHeader title="Brands" subtitle="Kelola brand yang tampil di navigasi & landing page brand"
        actions={<Button onClick={() => setEditing({ name: "", slug: "", description: "", logo: "", hero_image: "", seo_title: "", seo_description: "", published: true })} className={`${btnPrimary} h-10 px-4`} data-testid="new-brand-btn"><Plus size={14} /> Brand Baru</Button>} />

      <div className={`${card} overflow-hidden`}>
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-4 py-3">Nama</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Slug</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {loading ? <tr><td colSpan="4" className="text-center py-8 text-neutral-500">Memuat...</td></tr> :
              list.map((b) => (
                <tr key={b.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 font-semibold text-neutral-900">{b.name}</td>
                  <td className="px-4 py-3 text-neutral-500 font-mono text-xs hidden md:table-cell">{b.slug}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-mono uppercase ${b.published ? "bg-green-50 text-green-700" : "bg-neutral-100 text-neutral-500"}`}>
                      {b.published ? "Aktif" : "Arsip"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(b)} className="text-neutral-500 hover:text-[#FF9418] p-1.5" data-testid={`edit-brand-${b.slug}`}><Edit size={14} /></button>
                    <button onClick={() => del(b.id, b.name)} className="text-neutral-500 hover:text-red-600 p-1.5" data-testid={`del-brand-${b.slug}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {editing && <BrandModal title={editing.id ? "Edit Brand" : "Brand Baru"} data={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

/* ============ MODALS ============ */
function TaxonomyModal({ title, data, onClose, onSave }) {
  const [d, setD] = useState(data);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Maksimum 5 MB.");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return toast.error("Gunakan JPEG, PNG, atau WebP.");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: res } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", fileUrl(res.path));
      toast.success("Gambar kategori diunggah.");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal upload"); }
    finally { setUploading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg text-neutral-900">{title}</h2>
          <button onClick={onClose} className="text-neutral-500"><X size={18} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (!d.name) return toast.error("Nama wajib diisi"); onSave(d); }} className="space-y-3">
          <div><label className={label}>Nama *</label><input required value={d.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="cat-name" /></div>
          <div><label className={label}>Slug (kosongkan untuk auto)</label><input value={d.slug || ""} onChange={(e) => set("slug", e.target.value)} className={inp} placeholder="mis: bar-bender" data-testid="cat-slug" /></div>
          <div><label className={label}>Deskripsi</label><textarea rows={3} value={d.description || ""} onChange={(e) => set("description", e.target.value)} className={inp} /></div>
          <div>
            <label className={label}>Gambar Kategori (opsional)</label>
            {d.image ? (
              <div className="relative w-40 aspect-[4/3] bg-neutral-100 rounded-md overflow-hidden mb-2 group border border-neutral-200">
                <img src={d.image} alt="preview" className="w-full h-full object-cover" />
                <button type="button" onClick={() => set("image", "")} className="absolute top-1 right-1 bg-white/90 text-red-600 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={14} /></button>
              </div>
            ) : null}
            <label className="border-2 border-dashed border-neutral-300 hover:border-[#FF9418] p-3 rounded-md text-center cursor-pointer block max-w-[220px] transition text-xs">
              <Upload size={14} className="mx-auto mb-1 text-neutral-400" />
              <span className="text-neutral-700 font-semibold">{uploading ? "Mengunggah..." : "Upload gambar"}</span>
              <div className="text-[10px] text-neutral-500 mt-0.5">JPEG/PNG/WebP · maks 5 MB</div>
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} data-testid="cat-image-upload" />
            </label>
            <input value={d.image || ""} onChange={(e) => set("image", e.target.value)} placeholder="atau tempel URL gambar" className={`${inp} mt-2 text-xs`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Urutan</label><input type="number" value={d.order || 0} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className={inp} /></div>
            <div className="flex items-end"><label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={d.published !== false} onChange={(e) => set("published", e.target.checked)} className="accent-[#FF9418]" /> Aktif</label></div>
          </div>
          <div className="border-t border-neutral-200 pt-3 space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Navigasi</div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={d.show_in_menu !== false} onChange={(e) => set("show_in_menu", e.target.checked)} className="accent-[#FF9418]" data-testid="cat-show-in-menu" />
              Tampilkan di menu navigasi
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={d.show_on_homepage !== false} onChange={(e) => set("show_on_homepage", e.target.checked)} className="accent-[#FF9418]" data-testid="cat-show-on-homepage" />
              Tampilkan di homepage
            </label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" className={`${btnPrimary} h-10 px-5 flex-1`} data-testid="cat-save">Simpan</Button>
            <Button type="button" onClick={onClose} variant="outline" className="h-10 px-5 border-neutral-300">Batal</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BrandModal({ title, data, onClose, onSave }) {
  const [d, setD] = useState(data);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg text-neutral-900">{title}</h2>
          <button onClick={onClose} className="text-neutral-500"><X size={18} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (!d.name) return toast.error("Nama wajib diisi"); onSave(d); }} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Nama *</label><input required value={d.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="brand-name" /></div>
            <div><label className={label}>Slug</label><input value={d.slug || ""} onChange={(e) => set("slug", e.target.value)} className={inp} data-testid="brand-slug" /></div>
          </div>
          <div><label className={label}>Deskripsi</label><textarea rows={3} value={d.description || ""} onChange={(e) => set("description", e.target.value)} className={inp} /></div>
          <div><label className={label}>URL Logo</label><input value={d.logo || ""} onChange={(e) => set("logo", e.target.value)} className={inp} placeholder="/api/files/..." /></div>
          <div><label className={label}>URL Hero Image</label><input value={d.hero_image || ""} onChange={(e) => set("hero_image", e.target.value)} className={inp} /></div>
          <div><label className={label}>SEO Title</label><input value={d.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={inp} /></div>
          <div><label className={label}>Meta Description</label><textarea rows={2} value={d.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className={inp} /></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={d.published !== false} onChange={(e) => set("published", e.target.checked)} className="accent-[#FF9418]" /> Aktif</label>
          <div className="flex gap-2 pt-2">
            <Button type="submit" className={`${btnPrimary} h-10 px-5 flex-1`} data-testid="brand-save">Simpan</Button>
            <Button type="button" onClick={onClose} variant="outline" className="h-10 px-5 border-neutral-300">Batal</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
